import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, Ruler, ArrowRight, ArrowLeft } from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";

const TOOLS = [
  {
    id: "bone-age",
    name: "Bone Age Prediction",
    tagline: "AI-powered skeletal maturity from a hand X-ray",
    description:
      "Upload a left-hand radiograph. Our deep-learning model returns the child's bone age in seconds, with a consistency check against chronological age. Use this when you have an X-ray to interpret.",
    href: "/tools/bone-age",
    icon: Activity,
    color: "#4ECDC4",
    bullets: ["Single hand X-ray upload", "AI bone-age prediction", "Branded clinical PDF"],
  },
  {
    id: "growth",
    name: "Growth Assessment",
    tagline: "Predict adult height & assess growth (no X-ray needed)",
    description:
      "Enter the child's height, weight, bone age (from an X-ray report) and parental heights. We compute predicted adult height (Bayley-Pinneau + parental midpoint), APHV, GHI score and WHO growth-percentile position.",
    href: "/tools/growth",
    icon: Ruler,
    color: "#FFE66D",
    bullets: ["No X-ray upload required", "IAP-2015 percentile curves", "Predicted adult height + GHI"],
  },
];

const BoneAHChooserPage = () => {
  useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="max-w-5xl mx-auto px-6 pt-28 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#4ECDC4] transition-colors mb-6 text-sm" data-testid="back-to-home">
              <ArrowLeft className="w-4 h-4" /> Back to tots360
            </Link>
            <h1 className="font-display text-4xl md:text-5xl text-white mb-3" data-testid="chooser-title">
              Which tool would you like to use?
            </h1>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              We&apos;ve split <span className="text-[#4ECDC4]">BoneAH</span> into two focused tools so each one is faster and easier to use.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {TOOLS.map((tool, i) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <Link to={tool.href} data-testid={`chooser-card-${tool.id}`}>
                  <div
                    className="glass-card p-8 rounded-2xl h-full hover:scale-[1.02] transition-transform cursor-pointer border"
                    style={{ borderColor: `${tool.color}30` }}
                  >
                    <div
                      className="w-16 h-16 rounded-2xl mb-5 flex items-center justify-center"
                      style={{ backgroundColor: `${tool.color}15`, border: `1px solid ${tool.color}30` }}
                    >
                      <tool.icon className="w-8 h-8" style={{ color: tool.color }} />
                    </div>
                    <h2 className="font-display text-2xl text-white mb-2">{tool.name}</h2>
                    <p className="text-sm font-medium mb-4" style={{ color: tool.color }}>{tool.tagline}</p>
                    <p className="text-white/50 text-sm leading-relaxed mb-6">{tool.description}</p>
                    <ul className="space-y-2 mb-6">
                      {tool.bullets.map((b, j) => (
                        <li key={j} className="text-white/60 text-sm flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tool.color }} />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div
                      className="inline-flex items-center gap-2 font-semibold text-sm"
                      style={{ color: tool.color }}
                    >
                      Open {tool.name} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-xs text-white/30">
              Not sure which one? Most parents start with Growth Assessment if their child already has a bone-age value, or Bone Age Prediction if they have a fresh X-ray.
            </p>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default BoneAHChooserPage;
